import React, { useState, useMemo } from 'react';
import { ArrowLeftIcon, CheckCircleIcon, ChartBarIcon, FireIcon, TrophyIcon } from './icons/InterfaceIcons';
import { MockTestCard } from './MockTestCard';
import { MockTestFilters } from './MockTestFilters';
import { PastAttempts } from './PastAttempts';
import { calculateUserStats } from '../utils/userStats';

interface AssignedTest {
  test_id: number | string;
  name: string;
  duration: number;
  no_q: number;
}

const StatCard = ({ icon, label, value, colorClass }) => (
    <div className="flex-1 bg-brand-surface border border-brand-border rounded-lg p-4 flex items-center space-x-3">
        <div className={`p-2 rounded-full bg-opacity-10 ${colorClass}`}>
            {icon}
        </div>
        <div>
            <p className="text-sm text-brand-text-secondary">{label}</p>
            <p className="text-lg font-bold text-brand-text-primary">{value}</p>
        </div>
    </div>
);

export const MockTestList = ({ exam, onBack, user, onStartTest }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const assignedTests = useMemo(() => {
    if (!user || !user.tests?.assigned) return [];
    return Object.values(user.tests.assigned);
  }, [user]);

  const allTests = useMemo(() => {
    return (assignedTests as AssignedTest[]).map((test) => ({
        id: test.test_id.toString(),
        name: test.name,
        duration: test.duration,
        questions: test.no_q,
        type: 'Full Syllabus', // Default value
        difficulty: 'Medium', // Default value
        tags: ['New'], // Default value
        attempts: 0,
        bestScore: undefined,
        avgTime: undefined,
    }));
  }, [assignedTests]);

  const userStats = useMemo(() => calculateUserStats(user?.tests?.completed), [user]);

  const filteredTests = useMemo(() => {
    if (!searchQuery) return allTests;
    return allTests.filter(test => 
      test.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [allTests, searchQuery]);

  return (
    <div className="animate-fade-in space-y-8">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center space-x-2 text-sm font-semibold text-brand-text-secondary hover:text-brand-primary transition-colors">
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </button>
        <p className="text-sm text-brand-text-secondary">Home &gt; {exam.name} &gt; <span className="font-semibold text-brand-text-primary">Mock Tests</span></p>
      </div>

      <header className="bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-brand-text-primary">{exam.name} Mock Tests</h1>
        <p className="text-brand-text-secondary">Full syllabus & sectional mocks to sharpen your edge.</p>
        <div className="flex flex-wrap gap-4 pt-2">
            <StatCard icon={<CheckCircleIcon className="w-6 h-6"/>} label="Assigned Mocks" value={allTests.length} colorClass="text-brand-primary bg-brand-primary" />
            <StatCard icon={<ChartBarIcon className="w-6 h-6"/>} label="Avg. Accuracy" value={`${userStats.accuracy}%`} colorClass="text-brand-secondary bg-brand-secondary" />
            <StatCard icon={<FireIcon className="w-6 h-6"/>} label="Current Streak" value={`5 days`} colorClass="text-brand-accent-amber bg-brand-accent-amber" />
            <StatCard icon={<TrophyIcon className="w-6 h-6"/>} label="Percentile" value="85th" colorClass="text-brand-accent-violet bg-brand-accent-violet" />
        </div>
      </header>

      <MockTestFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8">
          {filteredTests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredTests.map(test => <MockTestCard key={test.id} test={test} onStartTest={onStartTest} />)}
            </div>
          ) : (
            <div className="text-center py-16 bg-brand-surface rounded-lg border border-brand-border">
                <h3 className="text-lg font-semibold text-brand-text-primary">No Assigned Tests Found</h3>
                <p className="text-brand-text-secondary mt-1">
                  {searchQuery ? 'Try adjusting your search query.' : 'New tests assigned to you will appear here.'}
                </p>
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-4 space-y-8">
          <PastAttempts completedTests={user?.tests?.completed} />
        </div>
      </div>
    </div>
  );
};
